import React, { useEffect, useRef } from 'react';
import { __ } from '@wordpress/i18n';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

// store
import { storeSet, useStoreValue } from '../../../../store';

// components
import StatusIconComponent from '../../../../components/StatusIconComponent';
import ButtonIconSimple from '../../../../components/ButtonWrapper/components/ButtonIconSimple';
import ButtonIconSimpleDoubleConfirmation from '../../../../components/ButtonWrapper/components/ButtonIconSimpleDoubleConfirmation';
import DeleteIcon from '../../../../icons/DeleteIcon';
import DownloadIcon from '../../../../icons/DownloadIcon';
import getFormattedLabel from '../../../../helpers/getFormattedLabel';
import BadgeTrack from '../../../../components/BadgeTrack';
import { cueSheetTypes } from '../../../../configData';
import SfxIcon from '../../../../icons/SfxIcon';
import MixIcon from '../../../../icons/MixIcon';
import ModalUploadCueSheetClip from '../../../../components/ModalUploadCueSheetClip';
import ClipService from '../../../../service/clip-service';
import displayNetworkError from '../../../../helpers/displayNetworkError';
import ModalAssignMemberToCuesheetItem from '../../../../components/ModalAssignMemberToCuesheetItem';

const ProjectCueSheet = ({ projectId, type, selectedItemId = null, onSelectItem = () => {} }) => {
    const cueSheet = useStoreValue('cueSheet');
    const cueType = cueSheetTypes[type];
    const finalCuseSheetItems = cueSheet.filter(o => o.cueType === cueType);
    const lastProjectId = useRef(null);

    const handleDeleteCueSheetItem = (id) => {
        storeSet('deleteCueSheetItem', projectId, id);
    }

    const handleDownloadCueSheetItem = async (clip) => {
        try {
            const data = await ClipService.downloadClipAsync(clip.id);
            const file = new Blob([data], { type: clip.mimeType });
            const url = window.URL.createObjectURL(file);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', clip.name);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            displayNetworkError(error);
            throw error;
        }
    }

    useEffect(() => {
        storeSet('fetchCueSheet', projectId);
        if (lastProjectId.current !== projectId) {
            lastProjectId.current = projectId;
            storeSet('refetchCueSheet', projectId);
        }
    }, [projectId]);

    return(
        <div className="projectCueSheet" role="table">
            <div className="projectCueSheet__header" role="rowgroup">
                <div className="projectCueSheet__row projectCueSheet__row--header" role="row">
                    <div className="projectCueSheet__cell projectCueSheet__cell--track" role="columnheader">{__('Track', 'soundbetween')}</div>
                    <div className="projectCueSheet__cell projectCueSheet__cell--titolo" role="columnheader">{__('Titolo', 'soundbetween')}</div>
                    <div className="projectCueSheet__cell projectCueSheet__cell--version" role="columnheader">{__('v', 'soundbetween')}</div>
                    <div className="projectCueSheet__cell projectCueSheet__cell--timecode" role="columnheader">{__('Timecode', 'soundbetween')}</div>
                    <div className="projectCueSheet__cell projectCueSheet__cell--assigned" role="columnheader">{__('Assegnato a', 'soundbetween')}</div>
                    <div className="projectCueSheet__cell projectCueSheet__cell--status" role="columnheader">{__('Status', 'soundbetween')}</div>
                    <div className="projectCueSheet__cell projectCueSheet__cell--actions" role="columnheader"></div>
                </div>
            </div>
            <OverlayScrollbarsComponent element="div" className="projectCueSheet__body" options={{ scrollbars: { autoHide: 'never' } }} defer role="rowgroup">
            <div className="projectCueSheet__list">
            {finalCuseSheetItems.map(o => {
                const isSelected = o.id === selectedItemId;
                const handleRowClick = () => onSelectItem(isSelected ? null : o);
                const handleRowKeyDown = (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick();
                    }
                };
                return <div
                    className={`projectCueSheet__row projectCueSheet__row--body${isSelected ? ' projectCueSheet__row--selected' : ''}`}
                    role="row"
                    key={o.id}
                    onClick={handleRowClick}
                    onKeyDown={handleRowKeyDown}
                    tabIndex={0}
                    aria-selected={isSelected}
                    style={{ cursor: 'pointer' }}>

                <div className="projectCueSheet__cell projectCueSheet__cell--track" role="cell">
                    {type === 'music' ? <BadgeTrack>{o.label}</BadgeTrack> : null}
                    {type === 'sfx' ? <SfxIcon/> : null}
                    {type === 'mix' ? <MixIcon/> : null}
                </div>
                <div className="projectCueSheet__cell projectCueSheet__cell--titolo" role="cell">
                    {o.title}
                </div>
                <div className="projectCueSheet__cell projectCueSheet__cell--version" role="cell">
                    {o.version}
                </div>
                <div className="projectCueSheet__cell projectCueSheet__cell--timecode" role="cell">
                    {o.timecode ?? '-:-'}
                </div>
                <div className="projectCueSheet__cell projectCueSheet__cell--assigned" role="cell">
                    <ModalAssignMemberToCuesheetItem projectId={projectId} cueSheetId={o.id} assignedUser={o.assignedUser ?? null}/>
                </div>
                <div className="projectCueSheet__cell projectCueSheet__cell--status" role="cell">
                    <StatusIconComponent status={o.status}></StatusIconComponent> {getFormattedLabel(o.status)}
                </div>
                <div className="projectCueSheet__cell projectCueSheet__cell--actions" role="cell">
                    <div className="projectCueSheet__actions">
                        <ButtonIconSimpleDoubleConfirmation
                            onConfirm={() => handleDeleteCueSheetItem(o.id)}
                            ariaLabel="Delete cue sheet item"
                            ariaLabelWarning="Click again to confirm deletion">
                            <DeleteIcon/>
                        </ButtonIconSimpleDoubleConfirmation>
                        {o.clip ? <ButtonIconSimple
                            onClick={() => handleDownloadCueSheetItem(o.clip)}>
                            <DownloadIcon/>
                        </ButtonIconSimple> : null}
                        {!o.clip
                            ? <ModalUploadCueSheetClip projectId={projectId} cueSheetItem={o}/>
                            : null}
                    </div>
                </div>
            </div>;
            })}
            </div>
            </OverlayScrollbarsComponent>
        </div>
    )
}

export default ProjectCueSheet;